import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit, FileText, FlaskConical, Leaf, Loader2, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import SoilResultsView from '../../components/orchard/SoilResultsView';
import NutrientTrendChart from '../../components/orchard/NutrientTrendChart';
import soilService from '../../services/soil';
import { errorMessage } from '../../utils/apiResponse';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { SAMPLE_TYPE_LABEL, depthText, trendAnalytes, trendSamples } from '../../utils/soilAnalytes';
import type { SoilSample, SoilSampleDetail as Detail } from '../../types/soil';

const SOURCE_TEXT = { manual: 'Typed in', csv: 'Read from the lab\'s CSV', ai: 'Read from the report by AI, then checked' } as const;

export default function SoilSampleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [sample, setSample] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [history, setHistory] = useState<SoilSample[]>([]);

  useEffect(() => {
    setSample(null);
    soilService.get(Number(id))
      .then((res) => { if (res.success && res.data) setSample(res.data); })
      .catch((e) => setError(errorMessage(e, 'Could not load the sample')));
  }, [id]);

  // Every sample of this kind in the block, for the trend; narrowed below to this place and depth.
  const blockId = sample?.block_id;
  const sampleType = sample?.sample_type;
  useEffect(() => {
    if (!blockId || !sampleType) return;
    soilService.list({ block_id: blockId, sample_type: sampleType })
      .then((r) => setHistory(r.success && r.data ? r.data : []))
      .catch(() => setHistory([]));
  }, [blockId, sampleType]);

  const trend = useMemo(() => (sample
    ? trendSamples(history, sample.sample_type ?? 'soil', sample.cultivar_area_id ?? null,
      sample.sample_type === 'leaf' ? undefined : { from: sample.depth_from_cm, to: sample.depth_to_cm })
    : []), [history, sample]);
  const hasTrend = !!sample && trendAnalytes(trend, sample.sample_type ?? 'soil').length > 0;

  const remove = async () => {
    setDeleting(true);
    try {
      await soilService.remove(Number(id));
      navigate(sample?.cultivar_area_id ? `/orchard/cultivar-areas/${sample.cultivar_area_id}` : `/orchard/blocks/${sample?.block_id}`, { replace: true });
    } catch (e) {
      setError(errorMessage(e, 'Could not delete the sample'));
      setDeleting(false);
    }
  };

  const place = sample
    ? [sample.farm_name, sample.block_name, sample.cultivar_area_name ?? 'whole block'].filter(Boolean).join(' › ')
    : 'Loading…';
  const facts = sample
    ? [
      ['Sampled', formatLocalDateInTz(sample.sampled_on, timezone)],
      ['Depth', depthText(sample.depth_from_cm, sample.depth_to_cm)],
      ['Tissue', sample.tissue],
      ['Growth stage', sample.growth_stage],
      ['Laboratory', sample.lab_name],
      ['Lab reference', sample.lab_reference],
      ['Name on report', sample.sample_label],
      ['Texture', sample.texture],
      ['Entered', `${SOURCE_TEXT[sample.source]}${sample.created_by_name ? `, by ${sample.created_by_name}` : ''}`],
    ].filter((f): f is [string, string] => !!f[1])
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`${SAMPLE_TYPE_LABEL[sample?.sample_type ?? 'soil']} sample`}
        subtitle={place}
        icon={sample?.sample_type === 'leaf' ? <Leaf className="h-6 w-6 text-white" /> : <FlaskConical className="h-6 w-6 text-white" />}
        showBackButton
        actionButtons={sample && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/orchard/soil/${sample.id}/edit`)}>
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            <Button size="sm" variant="danger" aria-label="Delete sample" onClick={() => setConfirming(true)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700 text-sm">{error}</p>
          </div>
        )}
        {!sample && !error && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>}

        {confirming && sample && (
          <div className="bg-error-50 border border-error-300 rounded-xl p-5">
            <p className="text-sm text-error-800 mb-3">Delete this sample and its results? The lab report stays with any other sample read from it.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="danger" isLoading={deleting} onClick={remove}>Delete sample</Button>
              <Button variant="outline" onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {sample && (
          <>
            <Card>
              <CardContent className="p-5 space-y-3">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {facts.map(([k, v]) => (
                    <div key={k} className="flex flex-wrap gap-x-2">
                      <dt className="text-neutral-500">{k}</dt>
                      <dd className="text-neutral-900 break-words min-w-0">{v}</dd>
                    </div>
                  ))}
                </dl>
                {sample.report_id && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline break-all text-left"
                    onClick={() => soilService.openReport(sample.report_id as number, sample.report_file_name)}
                  >
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    {sample.report_file_name ?? 'Lab report'}
                  </button>
                )}
                {sample.report_samples.length > 0 && (
                  <p className="text-xs text-neutral-500">
                    Also on this report:{' '}
                    {sample.report_samples.map((s, i) => (
                      <span key={s.id}>
                        {i > 0 && ', '}
                        <button type="button" className="text-primary-600 hover:underline" onClick={() => navigate(`/orchard/soil/${s.id}`)}>
                          {s.sample_label || s.cultivar_area_name || s.block_name}
                        </button>
                      </span>
                    ))}
                  </p>
                )}
                {sample.notes && <p className="text-sm text-neutral-700 whitespace-pre-line">{sample.notes}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-neutral-700">Results</h3>
                  {sample.previous && (
                    <p className="text-xs text-neutral-500">
                      Compared with{' '}
                      <button type="button" className="text-primary-600 hover:underline" onClick={() => navigate(`/orchard/soil/${sample.previous?.id}`)}>
                        {formatLocalDateInTz(sample.previous.sampled_on, timezone)}
                      </button>
                      {depthText(sample.previous.depth_from_cm, sample.previous.depth_to_cm) !== depthText(sample.depth_from_cm, sample.depth_to_cm)
                        && ' (a different depth)'}
                    </p>
                  )}
                </div>
                {sample.sample_type === 'leaf' && (
                  <p className="text-xs text-neutral-600">
                    {!sample.standards
                      ? 'This block grows more than one crop, so no one set of leaf standards applies. Record the sample against its planting to read it against that crop.'
                      : sample.standards.from_name
                        ? `Read against the leaf standards for ${sample.standards.from_name}${sample.standards.inherited ? ` (none are set for ${sample.standards.crop_type_name})` : ''}. `
                        : `No leaf standards are set for ${sample.standards.crop_type_name} yet. `}
                    {sample.standards && (
                      <button
                        type="button"
                        className="text-primary-600 hover:underline"
                        onClick={() => navigate(`/orchard/soil/removal?crop_type=${sample.standards?.from_crop_type_id ?? sample.standards?.crop_type_id}`)}
                      >
                        {sample.standards.from_name ? 'Change them' : 'Add them'}
                      </button>
                    )}
                  </p>
                )}
                <SoilResultsView
                  results={sample.results}
                  derived={sample.derived}
                  previous={sample.previous}
                  type={sample.sample_type}
                  standards={sample.standards?.ranges}
                />
              </CardContent>
            </Card>

            {hasTrend && (
              <Card>
                <CardContent className="p-5">
                  <NutrientTrendChart
                    samples={trend}
                    type={sample.sample_type ?? 'soil'}
                    initial={sample.results[0]?.analyte}
                    standards={sample.standards?.ranges}
                    caption={[sample.cultivar_area_name ?? `${sample.block_name}, whole block`,
                      depthText(sample.depth_from_cm, sample.depth_to_cm)].filter(Boolean).join(', ')}
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
